import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FieldType } from '@ngx-formly/core';
import { BaseDataValueMapItem } from '@core/models/base-data-value-map-item.model';
import { Observable } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { get, isEmpty } from 'lodash';
import { ProductConfigurationFacade } from '@features/client/order-workflow/facades/product-configuration.facade';
import { AttributeBaseDataService } from '@core/services/attribute-base-data.service';
import { DropdownImageComponent } from '@shared/components/dropdown-image/dropdown-image.component';
import { AsyncPipe } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { UploaderComponent } from '@shared/modules/uploader/containers/uploader/uploader.component';
import { TranslateDentalPipe } from '@shared/pipes/translate-dental.pipe';

@Component({
  selector: 'app-graphic-one',
  templateUrl: './graphic-one.component.html',
  imports: [
    ReactiveFormsModule,
    DropdownImageComponent,
    AsyncPipe,
    TranslatePipe,
    UploaderComponent,
    TranslateDentalPipe,
  ],
})
export class GraphicOneComponent extends FieldType implements OnInit {
  public colorAttributeKey = 'orthodontic_mouthguard_color_base_data_value_id';
  public fileAttributeKey = 'orthodontic_mouthguard_file_id';
  public customColors$: Observable<BaseDataValueMapItem[]>;
  public formGroup: FormGroup;
  public orderProductId: string;

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private readonly attributeBaseDataService: AttributeBaseDataService,
    private readonly productConfigurationFacade: ProductConfigurationFacade,
  ) {
    super();
  }

  public getDefaultFile(defaultValues: any): any {
    if (!defaultValues) {
      return;
    }

    const orderProduct = this.productConfigurationFacade.getOrderProductSnapshot();

    const existingFile = orderProduct?.files?.find((file) => {
      return file.id === defaultValues[this.fileAttributeKey];
    });

    return existingFile ? [existingFile] : [];
  }

  public ngOnInit(): void {
    this.customColors$ = this.attributeBaseDataService.listBaseData('orthodontic_mouthguard_color');
    this.orderProductId = this.productConfigurationFacade.getOrderProductSnapshot()?.id;

    this.initializeForm();
  }

  public updateFormControl(controlName: string, value: any): void {
    this.formControl?.setValue({
      ...this.formControl?.value,
      [controlName]: value,
    });
  }

  private initializeForm(): void {
    const defaultValue = !isEmpty(this.field?.defaultValue) ? this.field?.defaultValue : null;

    this.formGroup = new FormGroup({
      [this.colorAttributeKey]: new FormControl(get(defaultValue || null, this.colorAttributeKey)),
      [this.fileAttributeKey]: new FormControl(this.getDefaultFile(defaultValue)),
    });

    this.formGroup
      ?.get(this.colorAttributeKey)
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((changedValue) => {
        this.updateFormControl(this.colorAttributeKey, changedValue);
      });

    this.formGroup
      ?.get(this.fileAttributeKey)
      ?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((changedValue) => {
        this.updateFormControl(this.fileAttributeKey, changedValue?.[0]?.id);
      });
  }
}
